<template>
    <div class="mt-3">
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div class="hidden md:block"></div>
            <h1 class="text-3xl text-center">
                Marcas en total
                <strong class="text-theme-primary">
                    {{ itemsNumber }}
                </strong>
            </h1>
            <div class="flex justify-center md:justify-end">
                <app-button
                    v-if="
                        $ability.hasPermission('brand:Create') && !relevanceMode
                    "
                    color="success"
                    size="large"
                    @click="$router.push('/app/brands/new')"
                >
                    <span class="hidden md:inline">Nueva marca</span>
                    <span class="md:hidden font-bold">+</span>
                </app-button>
            </div>
        </div>

        <div class="flex flex-wrap mt-2">
            <div v-if="!relevanceMode" class="w-full mt-3">
                <common-filters
                    v-model="query"
                    :enabled-filters="$data.$enabledFilters"
                    :default-filters="$data.$defaultFilters"
                    @enter="reloadItems"
                />
                <div class="flex justify-center">
                    <app-button v-show="query" relief @click="reloadItems">
                        Consultar 🔎
                    </app-button>
                </div>
            </div>
            <div
                class="flex flex-col lg:flex-row items-center justify-between w-full mt-3 gap-4"
            >
                <div
                    class="flex flex-wrap items-center justify-center lg:justify-start gap-2"
                >
                    <tooltip-button
                        v-show="
                            selected.length &&
                            $ability.hasPermission('brand:Create') &&
                            !relevanceMode
                        "
                        icon="🗂️"
                        message="Clonar marca"
                        @click="cloneSelected"
                    />
                </div>

                <div
                    class="flex flex-wrap items-center justify-center lg:justify-end gap-3"
                >
                    <div v-if="relevanceMode" class="flex items-center gap-2">
                        <custom-checkbox v-model="reorderAll">
                            Afectar todo
                        </custom-checkbox>
                        <app-button color="success" @click="saveRelevance">
                            Guardar orden 💾
                        </app-button>
                    </div>

                    <app-button
                        v-if="$ability.hasPermission('brand:Update')"
                        :color="relevanceMode ? 'warn' : 'primary'"
                        @click="toggleRelevanceMode"
                    >
                        {{ relevanceMode ? "⚖️ Salir" : "⚖️ Editar orden" }}
                    </app-button>

                    <list-settings-button
                        v-if="!relevanceMode"
                        :value="{ pageSize }"
                        @change="onListSettingsChange"
                    />
                    <custom-pagination
                        v-model="currentPage"
                        :length="pagesNumber"
                        @input="listItems()"
                    />
                </div>
            </div>
        </div>

        <div class="w-full mt-2">
            <custom-sortable-table
                ref="selectableTable"
                :fields="activeFields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                :selectable="!relevanceMode"
                :draggable="relevanceMode"
                :external-current-page="currentPage"
                :external-total-pages="pagesNumber"
                :row-actions="rowActions"
                select-mode="multi"
                responsive
                no-border-collapse
                hover
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                @flex-selected="onRowSelected"
                @drag-drop="onDragDrop"
                @row-send-to-page="onRowSendToPage"
                @row-action="onRowAction"
            >
                <template #cell(slug)="data">
                    <router-link
                        :to="`/app/brands/detail/${data.item.slug}`"
                        class="flex flex-col items-center font-mono rounded-full w-min-16 px-1 py-1 text-white"
                        :class="
                            data.item.active
                                ? 'bg-theme-success'
                                : 'bg-theme-disabled'
                        "
                    >
                        <span class="text-xs font-mono">
                            {{ data.item.slug | cut(30, false) }}
                        </span>
                    </router-link>
                </template>

                <template #cell(name)="data">
                    <span class="text-center">
                        {{ data.item.name | cut(36) }}
                    </span>
                </template>

                <template #cell(summary)="data">
                    <small class="text-left break-all">
                        {{ data.item.summary | cut(30) }}
                    </small>
                </template>

                <template #cell(updatedAt)="data">
                    <small class="text-center">
                        {{ data.item.updatedAt | date }}
                    </small>
                </template>
            </custom-sortable-table>
        </div>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import { REORDER_PAGE_SIZE } from "@/common/constants";

import CommonFilters from "@/components/utils/CommonFilters.vue";
import CustomSortableTable from "@/components/utils/CustomSortableTable.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";

export default {
    name: "BrandsIndex",
    components: {
        CommonFilters,
        TooltipButton,
        CustomSortableTable
    },
    mixins: [urlQuerySyncMixin],
    layout: "app",
    meta: {
        roles: ["administrator", "support"]
    },
    data: () => ({
        query: {},
        keySearch: "",
        itemsCurrent: [],
        brandsTotal: [],
        selected: [],
        $fields: [
            { key: "slug", label: "Slug", sortable: true },
            { key: "name", label: "Nombre", sortable: true },
            {
                key: "summary",
                label: "Descripción",
                sortable: false
            },
            { key: "updatedAt", label: "📆 Actualización", sortable: true }
        ],
        $enabledFilters: ["keyword", "active"],
        $defaultFilters: ["keyword"],
        $urlPaginationKey: "page",
        $urlSortKey: "sort",
        $urlFilterSchema: [
            { key: "keyword", type: "string" },
            { key: "active", type: "boolean" }
        ],

        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        sortConfig: { orderBy: "updatedAt", orderType: "DESC" },
        relevanceMode: false,
        reorderAll: false,
        pageSize: 24
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        listPageSize() {
            return this.relevanceMode ? REORDER_PAGE_SIZE : this.pageSize;
        },
        rowActions() {
            const actions = [
                { key: "open", icon: "🔗", label: "Abrir en nueva pestaña" }
            ];
            if (this.selected.length > 1) {
                return actions;
            }
            if (this.$ability.hasPermission("brand:Create")) {
                actions.push({
                    key: "clone",
                    icon: "🗂️",
                    label: "Clonar marca"
                });
            }
            return actions;
        },
        activeFields() {
            if (this.relevanceMode) {
                return [
                    {
                        key: "weight",
                        label: "Peso",
                        sortable: false,
                        thClass: "w-14",
                        tdClass: "w-14"
                    },
                    {
                        key: "slug",
                        label: "Slug",
                        sortable: false,
                        thClass: "w-40",
                        tdClass: "w-40"
                    },
                    {
                        key: "name",
                        label: "Nombre",
                        sortable: false,
                        thClass: "w-96",
                        tdClass: "w-96"
                    },
                    {
                        key: "updatedAt",
                        label: "📆 Actualización",
                        sortable: false,
                        thClass: "w-36",
                        tdClass: "w-36"
                    }
                ];
            }
            return this.$data.$fields;
        }
    },
    watch: {},
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("brand:List", "/app"))) return;
        this.listItems(true);
        this._clickOutsideHandler = (e) => {
            if (
                this.selected.length &&
                this.$refs.selectableTable &&
                !this.$refs.selectableTable.$el.contains(e.target)
            ) {
                this.clearSelected();
            }
        };
        document.addEventListener("click", this._clickOutsideHandler);
    },
    beforeDestroy() {
        document.removeEventListener("click", this._clickOutsideHandler);
    },
    methods: {
        ...mapActions("brands", ["listBrandsPaginated", "updateBrandsWeights"]),
        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } = await this.listBrandsPaginated({
                    ...this.query,
                    page: this.currentPage,
                    itemsByPage: this.listPageSize,
                    complete,
                    orderBy: this.sortConfig.orderBy,
                    orderType: this.sortConfig.orderType
                });
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize }) {
            this.pageSize = pageSize;
            this.reloadItems();
        },
        onRowSelected(items) {
            this.selected = items;
        },
        clearSelected() {
            if (this.$refs.selectableTable) {
                this.$refs.selectableTable.clearSelected();
            }
            this.selected = [];
        },
        cloneSelected() {
            window
                .open(`/app/brands/clone/${this.selected[0].slug}`, "_blank")
                .focus();
        },
        onDragDrop(reorderedItems) {
            const offset = (this.currentPage - 1) * this.listPageSize;
            this.itemsCurrent = reorderedItems.map((item, index) => ({
                ...item,
                weight: offset + index + 1
            }));
        },
        async onRowSendToPage({ item, page }) {
            if (page === this.currentPage) return;
            const targetWeight = page * this.listPageSize - 1;
            this.itemsCurrent = this.itemsCurrent.filter(
                (p) => p.slug !== item.slug
            );
            this.$loading.show();
            try {
                await this.updateBrandsWeights({
                    brands: [{ slug: item.slug, weight: targetWeight }]
                });
                this.$sound.play();
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.itemsCurrent = [...this.itemsCurrent, item];
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onRowAction({ action, item }) {
            switch (action) {
                case "open":
                    window.open(`/app/brands/detail/${item.slug}`, "_blank");
                    break;
                case "clone":
                    window.open(`/app/brands/clone/${item.slug}`, "_blank");
                    break;
            }
        },
        toggleRelevanceMode() {
            this.relevanceMode = !this.relevanceMode;
            if (this.relevanceMode) {
                this.sortConfig = { orderBy: "weight", orderType: "ASC" };
                this.query = { active: true };
                this.clearSelected();
            } else {
                this.sortConfig = { orderBy: "updatedAt", orderType: "DESC" };
                this.query = {};
                this.reorderAll = false;
            }
            this.currentPage = 1;
            this.listItems(true);
        },
        async saveRelevance() {
            this.$loading.show();
            const offset = (this.currentPage - 1) * this.listPageSize;
            try {
                await this.updateBrandsWeights({
                    brands: this.itemsCurrent.map((item, index) => ({
                        slug: item.slug,
                        weight: offset + index + 1
                    })),
                    reorder: this.reorderAll || undefined
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Relevancia actualizada",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
